import React, { useContext } from "react";
import Helmet from "react-helmet";
import { TranslationContext } from "./translation/TranslationContext";
import { ALL_LANGUAGES } from "@src/i18next/constants";
import { shouldSkipLanguageRedirect } from "@src/config/englishOnlyUrls";

type SEOProps = {
  title?: string;
  description?: string;
  image?: string;
  noindex?: boolean;
  /** Robots meta tag value (e.g., "index, follow", "noindex, nofollow"). Overrides noindex if both are set. */
  robots?: string;
  twitter?: Record<string, any>;
  url?: string;
  noAppStoreBanner?: boolean;
  /** Current page path (without locale prefix) for hreflang tags, e.g. "/teachers/" */
  currentPath?: string;
  /** Whether this page has translations. If undefined, falls back to ENGLISH_ONLY_PATTERNS config */
  hasTranslations?: boolean;
};

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  image,
  noindex,
  robots,
  twitter,
  url,
  noAppStoreBanner,
  currentPath,
  hasTranslations,
}) => {
  const t = useContext(TranslationContext);

  // Compute translated title and description for reuse
  const translatedTitle = title ? (t.translateIfAble?.(title) as string) + " | ClassDojo" : "ClassDojo";
  const translatedDescription = description
    ? (t.translateIfAble?.(description) as string)
    : (t.translate?.("page_descriptions.default") as string);

  // Twitter meta: use explicit twitter prop if provided, otherwise fall back to page's title/description
  const twitterMeta = {
    card: "summary_large_image",
    site: "@classdojo",
    creator: "@classdojo",
    title: translatedTitle,
    description: translatedDescription,
    image: image || "https://static.classdojo.com/img/opengraph2026.jpg",
    ...twitter,
  };

  // Generate URL for a specific locale
  const getLocalizedUrl = (lang: string, path: string) => {
    const normalizedLang = lang.toLowerCase();
    // en-us is the default, so it doesn't have a locale prefix
    if (normalizedLang === "en-us" || normalizedLang === "en") {
      return `https://www.classdojo.com${path}`;
    }
    return `https://www.classdojo.com/${normalizedLang}${path}`;
  };

  // Determine if page should have hreflang tags
  // Priority: explicit prop > config file fallback > default true
  const shouldShowHreflang = hasTranslations ?? (currentPath ? !shouldSkipLanguageRedirect(currentPath) : true);

  return (
    <>
      {/* Hreflang tags for international SEO - only for pages with translations */}
      {currentPath && shouldShowHreflang && (
        <Helmet>
          {ALL_LANGUAGES.map((lang) => (
            <link key={lang} rel="alternate" hrefLang={lang.toLowerCase()} href={getLocalizedUrl(lang, currentPath)} />
          ))}
          {/* x-default for language negotiation fallback */}
          <link rel="alternate" hrefLang="x-default" href={`https://www.classdojo.com${currentPath}`} />
        </Helmet>
      )}

      <Helmet>
        <title>{translatedTitle}</title>
        <meta itemProp="name" content={translatedTitle} />
        <meta property="og:title" content={translatedTitle} />
      </Helmet>

      <Helmet>
        <meta name="description" content={translatedDescription} />
        <meta itemProp="description" content={translatedDescription} />
        <meta property="og:description" content={translatedDescription} />
      </Helmet>

      {image ? (
        <Helmet>
          <meta property="og:image" content={image} />
        </Helmet>
      ) : (
        <Helmet>
          <meta property="og:image" content="https://static.classdojo.com/img/opengraph2026.jpg" />
        </Helmet>
      )}

      {(robots || noindex) && (
        <Helmet>
          <meta name="robots" content={robots || "noindex"} />
          <meta name="googlebot" content={robots || "noindex"} />
        </Helmet>
      )}

      {url && (
        <Helmet>
          <meta property="og:url" content={url} />
        </Helmet>
      )}

      <Helmet>
        <meta name="twitter:card" content={twitterMeta.card} />
        <meta name="twitter:site" content={twitterMeta.site} />
        <meta name="twitter:creator" content={twitterMeta.creator} />
        <meta name="twitter:title" content={twitterMeta.title} />
        <meta name="twitter:description" content={twitterMeta.description} />
        <meta name="twitter:image" content={twitterMeta.image} />
        <meta name="twitter:dnt" content="on" />
      </Helmet>

      <Helmet>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        {noAppStoreBanner ? null : <meta name="apple-itunes-app" content="app-id=552602056" />}
        <meta name="HandheldFriendly" content="True" />
        <meta name="author" content="ClassDojo" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ClassDojo" />
        <meta property="fb:app_id" content="171634556373284" />
        <meta name="google-site-verification" content="Av0W9Fr00yNaww-mYNrSwsA8HRRHYn1Ipm_XYURVVVk" />
        <link rel="icon" href={"/favicon.svg"} sizes="any" type="image/svg+xml" />
        <link rel="icon" href={"/favicon-48.png"} sizes="16x16 32x32 48x48" type="image/png" />
        <link rel="icon" href={"/favicon.png"} sizes="64x64 128x128 192x192" type="image/png" />
        <link rel="apple-touch-icon" href={"/apple-touch-icon.png"} />
      </Helmet>
    </>
  );
};

export default SEO;
